import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

// ─── Helpers ──────────────────────────────────────────────────────────────

function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function applyReplacements(text, matches, selected) {
  if (!matches.length) return escHtml(text).replace(/\n/g, '<br>');
  let result = '';
  let cursor = 0;
  matches.forEach((m, idx) => {
    if (m.offset < cursor) return;
    result += escHtml(text.slice(cursor, m.offset));
    const original = text.slice(m.offset, m.offset + m.length);
    const chosen = selected[idx] ?? (m.replacements?.[0]?.value ?? original);
    if (chosen !== original) {
      result += `<span class="gf-fixed" title="${escHtml(m.message)}">${escHtml(chosen)}</span>`;
    } else {
      result += escHtml(chosen);
    }
    cursor = m.offset + m.length;
  });
  result += escHtml(text.slice(cursor));
  return result.replace(/\n/g, '<br>');
}

function highlightContext(ctx) {
  if (!ctx) return '';
  const { text, offset, length } = ctx;
  return (
    escHtml(text.slice(0, offset)) +
    `<mark>${escHtml(text.slice(offset, offset + length))}</mark>` +
    escHtml(text.slice(offset + length))
  );
}

// ─── Component ───────────────────────────────────────────────────────────

const GrammarFixPage = () => {
  const [inputText, setInputText] = useState('');
  const [checkedText, setCheckedText] = useState('');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState('idle'); // idle | checking | done | error
  const [copyLabel, setCopyLabel] = useState('Copy');

  const debounceTimer = useRef(null);
  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const requestSeq = useRef(0);

  const charCount = inputText.length;
  const wordCount = inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
  const issueCount = status === 'done' ? matches.length : 0;

  const runCheck = useCallback(async (text) => {
    if (!text.trim()) {
      setStatus('idle');
      setMatches([]);
      setCheckedText('');
      return;
    }
    const seq = ++requestSeq.current;
    setStatus('checking');
    try {
      const res = await fetch('https://api.languagetool.org/v2/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ text, language: 'en-US' }),
      });
      if (!res.ok) throw new Error('bad status');
      const data = await res.json();
      if (seq !== requestSeq.current) return; // stale response
      setMatches(data.matches || []);
      setSelected({});
      setCheckedText(text);
      setStatus('done');
    } catch (_) {
      if (seq !== requestSeq.current) return;
      setStatus('error');
    }
  }, []);

  const handleInput = (e) => {
    const text = e.target.value;
    setInputText(text);
    clearTimeout(debounceTimer.current);
    if (!text.trim()) {
      requestSeq.current++;
      setStatus('idle');
      setMatches([]);
      setCheckedText('');
      return;
    }
    debounceTimer.current = setTimeout(() => {
      if (text.trim().length >= 8) runCheck(text);
    }, 1200);
  };

  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  const handleCheckNow = () => {
    clearTimeout(debounceTimer.current);
    runCheck(inputText);
  };

  const handleClear = () => {
    requestSeq.current++;
    setInputText('');
    setCheckedText('');
    setMatches([]);
    setSelected({});
    setStatus('idle');
    inputRef.current?.focus();
  };

  const chooseReplacement = (idx, value) => {
    setSelected((prev) => ({ ...prev, [idx]: value }));
  };

  const handleCopy = async () => {
    const text = outputRef.current?.innerText?.trim();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy'), 1500);
  };

  const correctedHtml = checkedText
    ? applyReplacements(checkedText, matches, selected)
    : '<span class="gf-placeholder">Corrected sentence will appear here…</span>';

  return (
    <div className="gf-page">
      <Helmet>
        <title>Grammar Fix – Online Sentence Grammar Checker | AStudyHub</title>
        <meta name="description" content="Free online grammar checker. Fix grammar, spelling and punctuation mistakes in your sentences instantly with clear suggestions and explanations." />
        <meta name="keywords" content="grammar fix, grammar checker online, sentence grammar checker, fix grammar mistakes, english grammar checker, punctuation checker, spelling checker, correct my sentence" />
        <link rel="canonical" href="https://www.astudyhub.org/grammar-fix" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.astudyhub.org/grammar-fix" />
        <meta property="og:title" content="Grammar Fix – Online Sentence Grammar Checker | AStudyHub" />
        <meta property="og:description" content="Free online grammar checker. Fix grammar, spelling and punctuation mistakes in your sentences instantly." />
        <meta property="og:image" content="https://www.astudyhub.org/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Grammar Fix – Online Sentence Grammar Checker | AStudyHub" />
        <meta name="twitter:description" content="Free online grammar checker. Fix grammar, spelling and punctuation mistakes in your sentences instantly." />
        <meta name="twitter:image" content="https://www.astudyhub.org/logo512.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Grammar Fix",
            "url": "https://www.astudyhub.org/grammar-fix",
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free online grammar checker. Fix grammar, spelling and punctuation mistakes in your sentences instantly.",
            "isPartOf": { "@type": "WebSite", "name": "AStudyHub", "url": "https://www.astudyhub.org" }
          })}
        </script>
      </Helmet>
      <style>{`
        .gf-page {
          font-family: 'Roboto', sans-serif;
          background: #f8f9fa;
          min-height: 60vh;
          margin-top: 136px;
          padding: 32px 24px 48px;
        }
        .gf-hero { margin-bottom: 28px; }
        .gf-hero h1 {
          font-size: 1.8rem;
          font-weight: 400;
          color: #202124;
          margin-bottom: 6px;
        }
        .gf-hero p { color: #5f6368; font-size: .95rem; }
        .gf-lang-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e8f5ee;
          border-radius: 20px;
          padding: 4px 14px;
          font-size: .85rem;
          font-weight: 500;
          color: #1c7049;
          margin-bottom: 12px;
        }
        .gf-editor-wrap {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 1px 3px rgba(28,112,73,.15), 0 4px 8px rgba(28,112,73,.1);
          margin-bottom: 20px;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .gf-panel { display: flex; flex-direction: column; min-height: 260px; }
        .gf-panel-input { border-right: 1px solid #c2d9cc; }
        .gf-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid #c2d9cc;
          background: #f4faf7;
        }
        .gf-panel-label {
          font-size: .78rem;
          font-weight: 500;
          color: #1c7049;
          text-transform: uppercase;
          letter-spacing: .6px;
        }
        .gf-panel-actions { display: flex; gap: 8px; }
        .gf-typing-area {
          flex: 1;
          width: 100%;
          padding: 16px;
          border: none;
          outline: none;
          resize: none;
          font-family: 'Roboto', sans-serif;
          font-size: 1.05rem;
          line-height: 1.7;
          color: #202124;
          background: transparent;
          min-height: 220px;
        }
        .gf-typing-area::placeholder { color: #a8c4b5; }
        .gf-output-area {
          flex: 1;
          padding: 16px;
          font-size: 1.05rem;
          line-height: 1.8;
          color: #202124;
          min-height: 220px;
          word-break: break-word;
          white-space: pre-wrap;
        }
        .gf-placeholder { color: #a8c4b5; font-size: 1rem; }
        .gf-fixed {
          background: #d7f0e0;
          color: #1c7049;
          border-radius: 3px;
          padding: 0 2px;
          font-weight: 500;
        }
        .gf-btn-ghost {
          padding: 4px 12px;
          border: 1px solid #c2d9cc;
          border-radius: 4px;
          background: transparent;
          color: #1c7049;
          font-size: .8rem;
          font-weight: 500;
          cursor: pointer;
          transition: background .15s, color .15s, border-color .15s;
        }
        .gf-btn-ghost:hover { background: #1c7049; color: #fff; border-color: #1c7049; }
        .gf-btn-solid {
          padding: 4px 14px;
          border: 1px solid #1c7049;
          border-radius: 4px;
          background: #1c7049;
          color: #fff;
          font-size: .8rem;
          font-weight: 500;
          cursor: pointer;
          transition: background .15s, opacity .15s;
        }
        .gf-btn-solid:hover { background: #17593a; }
        .gf-btn-solid:disabled { opacity: .5; cursor: default; }
        .gf-status {
          padding: 8px 16px;
          font-size: .8rem;
          color: #4a7a62;
          border-top: 1px solid #eaf4ef;
          background: #f4faf7;
        }
        .gf-status.gf-status-error { color: #b3261e; background: #fdecea; }
        .gf-stats-bar {
          display: flex;
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          margin-bottom: 24px;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .gf-stat {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 14px 8px;
          border-right: 1px solid #c2d9cc;
        }
        .gf-stat:last-child { border-right: none; }
        .gf-stat-label { font-size: .72rem; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; color: #4a7a62; margin-bottom: 4px; }
        .gf-stat-val { font-size: 1.4rem; font-weight: 400; color: #1c7049; letter-spacing: -.5px; }
        .gf-issues {
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          max-width: 1000px;
          margin: 0 auto 24px;
        }
        .gf-issues-header {
          padding: 14px 20px;
          font-size: .9rem;
          font-weight: 500;
          color: #202124;
          border-bottom: 1px solid #c2d9cc;
          background: #f4faf7;
        }
        .gf-issue {
          padding: 14px 20px;
          border-bottom: 1px solid #eaf4ef;
        }
        .gf-issue:last-child { border-bottom: none; }
        .gf-issue-msg { font-size: .9rem; color: #202124; margin-bottom: 6px; }
        .gf-issue-context {
          font-family: 'Roboto Mono', 'Courier New', monospace;
          font-size: .85rem;
          color: #5f6368;
          background: #f8f9fa;
          border-radius: 4px;
          padding: 8px 10px;
          margin-bottom: 8px;
        }
        .gf-issue-context mark { background: #fde3e1; color: #b3261e; border-radius: 2px; padding: 0 2px; }
        .gf-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .gf-chip {
          padding: 5px 12px;
          border-radius: 18px;
          border: 1.5px solid #1c7049;
          background: #fff;
          color: #1c7049;
          font-size: .85rem;
          cursor: pointer;
          transition: background .15s, color .15s;
        }
        .gf-chip:hover { background: #1c7049; color: #fff; }
        .gf-chip.gf-chip-active { background: #1c7049; color: #fff; }
        .gf-chip.gf-chip-keep { border-style: dashed; color: #5f6368; border-color: #c2d9cc; }
        .gf-chip.gf-chip-keep:hover { background: #5f6368; color: #fff; border-color: #5f6368; }
        .gf-no-issues { padding: 20px; text-align: center; color: #1c7049; font-size: .95rem; }
        .gf-reference {
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .gf-reference summary {
          padding: 14px 20px;
          font-size: .9rem;
          font-weight: 500;
          cursor: pointer;
          color: #4a7a62;
          list-style: none;
          display: flex;
          align-items: center;
          user-select: none;
        }
        .gf-reference summary::-webkit-details-marker { display: none; }
        .gf-reference summary::before { content: '▶'; font-size: .65rem; margin-right: 8px; transition: transform .2s; }
        .gf-reference[open] summary::before { transform: rotate(90deg); }
        .gf-reference summary:hover { background: #f4faf7; color: #1c7049; }
        .gf-ref-table {
          width: 100%;
          border-collapse: collapse;
          font-size: .88rem;
        }
        .gf-ref-table td { padding: 8px 20px; border-top: 1px solid #eaf4ef; color: #202124; vertical-align: top; }
        .gf-ref-table td:first-child { color: #b3261e; width: 40%; }
        .gf-ref-table td:last-child { color: #1c7049; }
        @media (max-width: 680px) {
          .gf-page { padding: 20px 16px 40px; }
          .gf-editor-wrap { grid-template-columns: 1fr; }
          .gf-panel-input { border-right: none; border-bottom: 1px solid #c2d9cc; }
          .gf-hero h1 { font-size: 1.4rem; }
          .gf-stats-bar { flex-wrap: wrap; }
          .gf-stat { flex: 1 1 calc(50% - 1px); }
          .gf-stat:nth-child(2) { border-right: none; }
        }
      `}</style>

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div className="gf-hero">
          <div className="gf-lang-pill">
            <span>Grammar</span>
            <span>→</span>
            <span>Fixed Sentence</span>
          </div>
          <h1>Grammar Fix</h1>
          <p>Type or paste your sentence and instantly find and fix grammar, spelling and punctuation mistakes.</p>
        </div>

        <div className="gf-editor-wrap">
          {/* Input panel */}
          <div className="gf-panel gf-panel-input">
            <div className="gf-panel-header">
              <span className="gf-panel-label">Your Sentence</span>
              <div className="gf-panel-actions">
                <button className="gf-btn-ghost" onClick={handleClear}>Clear</button>
                <button className="gf-btn-solid" onClick={handleCheckNow} disabled={!inputText.trim() || status === 'checking'}>
                  {status === 'checking' ? 'Checking…' : 'Check Grammar'}
                </button>
              </div>
            </div>
            <textarea
              ref={inputRef}
              className="gf-typing-area"
              value={inputText}
              placeholder="Type or paste a sentence here… e.g. She dont like going to the market yesterday."
              spellCheck={false}
              onChange={handleInput}
            />
            {status === 'error' && (
              <div className="gf-status gf-status-error">Unable to check grammar right now. Please try again in a moment.</div>
            )}
          </div>

          {/* Output panel */}
          <div className="gf-panel">
            <div className="gf-panel-header">
              <span className="gf-panel-label">Corrected Sentence</span>
              <div className="gf-panel-actions">
                <button className="gf-btn-ghost" onClick={handleCopy}>{copyLabel}</button>
              </div>
            </div>
            <div
              ref={outputRef}
              className="gf-output-area"
              dangerouslySetInnerHTML={{ __html: correctedHtml }}
            />
          </div>
        </div>

        {/* Stats bar */}
        <div className="gf-stats-bar">
          <div className="gf-stat"><span className="gf-stat-label">Characters</span><span className="gf-stat-val">{charCount}</span></div>
          <div className="gf-stat"><span className="gf-stat-label">Words</span><span className="gf-stat-val">{wordCount}</span></div>
          <div className="gf-stat"><span className="gf-stat-label">Issues Found</span><span className="gf-stat-val">{issueCount}</span></div>
        </div>

        {/* Issues list */}
        {status === 'done' && (
          <div className="gf-issues">
            <div className="gf-issues-header">
              {matches.length ? `${matches.length} issue${matches.length > 1 ? 's' : ''} found` : 'Issues'}
            </div>
            {matches.length === 0 ? (
              <div className="gf-no-issues">✅ No grammar issues found! Your sentence looks good.</div>
            ) : (
              matches.map((m, idx) => {
                const original = checkedText.slice(m.offset, m.offset + m.length);
                const activeValue = selected[idx] ?? (m.replacements?.[0]?.value ?? original);
                return (
                  <div className="gf-issue" key={idx}>
                    <div className="gf-issue-msg">{m.message}</div>
                    {m.context && (
                      <div className="gf-issue-context" dangerouslySetInnerHTML={{ __html: highlightContext(m.context) }} />
                    )}
                    <div className="gf-chips">
                      {(m.replacements || []).slice(0, 5).map((r, ri) => (
                        <button
                          key={ri}
                          className={`gf-chip${activeValue === r.value ? ' gf-chip-active' : ''}`}
                          onClick={() => chooseReplacement(idx, r.value)}
                        >
                          {r.value}
                        </button>
                      ))}
                      <button
                        className={`gf-chip gf-chip-keep${activeValue === original ? ' gf-chip-active' : ''}`}
                        onClick={() => chooseReplacement(idx, original)}
                      >
                        Keep original
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Quick reference */}
        <details className="gf-reference">
          <summary>Quick Reference – Common Grammar Mistakes</summary>
          <table className="gf-ref-table">
            <tbody>
              <tr><td>She don't like it.</td><td>She doesn't like it.</td></tr>
              <tr><td>He go to school everyday.</td><td>He goes to school every day.</td></tr>
              <tr><td>I have went there.</td><td>I have gone there.</td></tr>
              <tr><td>There are many student in the class.</td><td>There are many students in the class.</td></tr>
              <tr><td>Its a good day.</td><td>It's a good day.</td></tr>
              <tr><td>Me and him went to the market.</td><td>He and I went to the market.</td></tr>
              <tr><td>She is more taller than me.</td><td>She is taller than me.</td></tr>
              <tr><td>I would of done it.</td><td>I would have done it.</td></tr>
            </tbody>
          </table>
        </details>
      </div>
    </div>
  );
};

export default GrammarFixPage;
